import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from 'components';
import { useAdminUsersQuery, useLeadByIdQuery, useUpdateLeadAssignmentMutation } from 'api';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { getUpdatedPayload } from '../../../../../../../utils/helpers';

export const useAssignmentEdit = (props: { onCancelModal: () => void; open: boolean }) => {
  const { onCancelModal, open } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const { data: leadDetails = {} } = useLeadByIdQuery({ id });
  const { data: userData } = useAdminUsersQuery();
  const [updateLeadAssignment, { isLoading }] = useUpdateLeadAssignmentMutation();
  const { handleSuccess, handleError } = useNotificationMessages();

  const initialAssignment: Record<string, string> = useMemo(
    () => ({
      administratorId: leadDetails.administratorId || '',
      salesManagerId: leadDetails.salesManagerId || '',
      salesManagerAssistantId: leadDetails.salesManagerAssistantId || '',
      purchaseManagerId: leadDetails.purchaseManagerId || '',
      purchaseManagerAssistantId: leadDetails.purchaseManagerAssistantId || '',
    }),
    [leadDetails]
  );

  useEffect(() => {
    if (initialAssignment && open) {
      form.setFieldsValue(initialAssignment);
    }
  }, [form, open, initialAssignment]);

  const onCancel = useCallback(() => {
    form.resetFields();
    onCancelModal();
  }, [form, onCancelModal]);

  const onFinish = useCallback(async () => {
    try {
      let payload = form.getFieldsValue() || {};
      const isFormChanged = Object.keys(payload).some((key) => initialAssignment[key] !== payload[key]);

      if (!isFormChanged) {
        onCancel();
        return;
      }
      payload = getUpdatedPayload(payload, initialAssignment);
      payload.id = id as string;
      const { data, error }: Record<string, any> = await updateLeadAssignment(payload);
      if (data?.success) {
        handleSuccess('Lead assignments were successfully updated.');
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      console.error('Error updating lead assignments:', err);
      handleError(err);
    }
  }, [updateLeadAssignment, handleSuccess, handleError, id, onCancel, form, initialAssignment]);

  return {
    onFinish,
    leadDetails,
    userData,
    form,
    isLoading,
    onCancel,
  };
};
